/**
 * QuickAddProduct modal for creating a new simple product from within the POS.
 *
 * Renders a compact modal form with fields for name, price, SKU, category,
 * and stock quantity. On success the newly created product object is passed
 * back to the parent via `onProductCreated` so the caller can add it to the
 * cart or refresh the product grid.
 *
 * @param {Object}   props
 * @param {Function} props.onClose          - Callback to close the modal.
 * @param {Function} props.onProductCreated - Callback fired with the new product object on success.
 * @param {Array}    props.categories       - Array of { id, name, slug } category objects.
 */
import { useState, useCallback } from 'react';
import api from '../../api/client';

export default function QuickAddProduct({ onClose, onProductCreated, categories }) {
  // ── Form state ──────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [sku, setSku] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [stockQuantity, setStockQuantity] = useState('');

  // ── UI state ────────────────────────────────────────────────────
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [createdProduct, setCreatedProduct] = useState(null);

  /**
   * Validate required fields. Returns true when valid.
   */
  const validate = useCallback(() => {
    const next = {};

    if (!name.trim()) {
      next.name = 'Product name is required.';
    }

    if (!price.trim()) {
      next.price = 'Price is required.';
    } else if (isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      next.price = 'Please enter a valid price.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }, [name, price]);

  /**
   * Clear a single field error when the user starts typing.
   */
  const clearError = useCallback((field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (apiError) setApiError('');
  }, [apiError]);

  /**
   * Submit the form to the REST API.
   */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setApiError('');

    const data = {
      name: name.trim(),
      regular_price: parseFloat(price).toFixed(2),
      sku: sku.trim(),
    };

    if (categoryId) {
      data.category_id = parseInt(categoryId, 10);
    }

    if (stockQuantity.trim() !== '') {
      data.stock_quantity = parseInt(stockQuantity, 10);
      data.manage_stock = true;
    }

    try {
      const product = await api.createProduct(data);
      setCreatedProduct(product);
    } catch (err) {
      const message =
        err?.message ||
        err?.data?.message ||
        'Failed to create product. Please try again.';
      setApiError(message);
    } finally {
      setSubmitting(false);
    }
  }, [name, price, sku, categoryId, stockQuantity, validate]);

  const handleAddToCart = useCallback(() => {
    if (createdProduct && typeof onProductCreated === 'function') {
      onProductCreated(createdProduct);
    }
    onClose();
  }, [createdProduct, onProductCreated, onClose]);

  const handleAddAnother = useCallback(() => {
    setCreatedProduct(null);
    setName('');
    setPrice('');
    setSku('');
    setCategoryId('');
    setStockQuantity('');
    setErrors({});
    setApiError('');
  }, []);

  /**
   * Close when clicking the backdrop overlay.
   */
  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  // ── Shared class strings ────────────────────────────────────────
  const labelClasses = 'block text-sm font-medium text-pos-foreground mb-1';

  const inputClasses =
    'block w-full rounded-md border border-pos-border bg-white px-3 py-2 text-sm text-pos-foreground ' +
    'placeholder-pos-muted-fg outline-none transition-shadow ' +
    'focus:border-pos-primary focus:ring-2 focus:ring-pos-primary/20';

  const errorInputClasses =
    'block w-full rounded-md border border-red-400 bg-white px-3 py-2 text-sm text-pos-foreground ' +
    'placeholder-pos-muted-fg outline-none transition-shadow ' +
    'focus:border-red-500 focus:ring-2 focus:ring-red-200';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Add Product"
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl">
        {/* ── Header ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-b border-pos-border px-5 py-4">
          <h2 className="text-lg font-semibold text-pos-foreground">Add Product</h2>
          <button
            type="button"
            onClick={onClose}
            className={
              'flex-shrink-0 rounded-lg p-1.5 text-pos-muted-fg transition-colors ' +
              'hover:bg-pos-muted hover:text-pos-foreground ' +
              'focus:outline-none focus:ring-2 focus:ring-pos-primary/30'
            }
            aria-label="Close"
          >
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* ── Error banner ───────────────────────────────────── */}
        {apiError && (
          <div
            className="mx-5 mt-4 flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700"
            role="alert"
          >
            <svg
              className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{apiError}</span>
          </div>
        )}

        {/* ── Success state ─────────────────────────────────── */}
        {createdProduct && (
          <div className="px-5 py-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-pos-primary/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-pos-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-pos-foreground">{createdProduct.name}</p>
                <p className="text-xs text-pos-muted-fg">Added to WooCommerce</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleAddToCart}
                className="flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white bg-pos-primary hover:bg-pos-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-pos-primary/50 focus:ring-offset-1"
              >
                Add to Cart
              </button>
              <button
                type="button"
                onClick={handleAddAnother}
                className="flex-1 rounded-lg border border-pos-border px-4 py-2.5 text-sm font-medium text-pos-foreground hover:bg-pos-muted transition-colors focus:outline-none focus:ring-2 focus:ring-pos-primary/20 focus:ring-offset-1"
              >
                Add Another
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 rounded-lg p-2.5 text-pos-muted-fg hover:text-pos-foreground hover:bg-pos-muted transition-colors focus:outline-none"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ── Form body ──────────────────────────────────────── */}
        {!createdProduct && <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-4 px-5 py-5">
            {/* Name (required) */}
            <div>
              <label htmlFor="symple-pos-qap-name" className={labelClasses}>
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="symple-pos-qap-name"
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); clearError('name'); }}
                placeholder="Product name"
                className={errors.name ? errorInputClasses : inputClasses}
                autoFocus
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Price (required) */}
            <div>
              <label htmlFor="symple-pos-qap-price" className={labelClasses}>
                Price <span className="text-red-500">*</span>
              </label>
              <input
                id="symple-pos-qap-price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => { setPrice(e.target.value); clearError('price'); }}
                placeholder="0.00"
                className={errors.price ? errorInputClasses : inputClasses}
              />
              {errors.price && (
                <p className="mt-1 text-xs text-red-600">{errors.price}</p>
              )}
            </div>

            {/* SKU (optional) */}
            <div>
              <label htmlFor="symple-pos-qap-sku" className={labelClasses}>
                SKU
              </label>
              <input
                id="symple-pos-qap-sku"
                type="text"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                placeholder="e.g. OAK-DT-001"
                className={inputClasses}
              />
            </div>

            {/* Category (optional) */}
            <div>
              <label htmlFor="symple-pos-qap-category" className={labelClasses}>
                Category
              </label>
              <select
                id="symple-pos-qap-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className={inputClasses}
              >
                <option value="">No category</option>
                {Array.isArray(categories) && categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Stock Quantity (optional) */}
            <div>
              <label htmlFor="symple-pos-qap-stock" className={labelClasses}>
                Stock Quantity
              </label>
              <input
                id="symple-pos-qap-stock"
                type="number"
                min="0"
                step="1"
                value={stockQuantity}
                onChange={(e) => setStockQuantity(e.target.value)}
                placeholder="Leave blank for unlimited"
                className={inputClasses}
              />
            </div>
          </div>

          {/* ── Footer ─────────────────────────────────────────── */}
          <div className="flex items-center justify-end gap-3 border-t border-pos-border px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={
                'rounded-lg border border-pos-border px-4 py-2 text-sm font-medium text-pos-foreground ' +
                'transition-colors hover:bg-pos-muted ' +
                'focus:outline-none focus:ring-2 focus:ring-pos-primary/20 focus:ring-offset-1 ' +
                'disabled:opacity-50 disabled:cursor-not-allowed'
              }
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className={
                'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white ' +
                'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ' +
                (submitting
                  ? 'bg-pos-primary/70 cursor-not-allowed focus:ring-pos-primary/40'
                  : 'bg-pos-primary hover:bg-pos-primary-dark focus:ring-pos-primary')
              }
            >
              {submitting && (
                <svg
                  className="h-4 w-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              {submitting ? 'Adding...' : 'Add Product'}
            </button>
          </div>
        </form>}
      </div>
    </div>
  );
}
